import React from 'react'

import { Box, NumberBadge, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/factorial-one-react/dist/experimental'

import NumberField from 'modules/core/components/NumberField'

import {
  NumberAnswerProps,
  Participant,
  Question,
  QuestionType,
} from 'modules/form_answers/lib/types'

import { MultipleChoiceAnswer } from '../Answers/MultipleChoice'
import { RatingAnswer } from '../Answers/Rating'
import { SingleChoiceAnswer } from '../Answers/SingleChoice'

interface Props {
  participant: Participant | null
  data: (
    | {
        itemType: 'section'
        id: string
        title: string
        questions: Question[]
        position: number
      }
    | {
        itemType: 'question'
        id: string
        label: string
        questionType: QuestionType
        mandatory: boolean
        position: number
        scale: { value: string; text: string }[]
      }
  )[]
  question?: Question | null
}

interface AnswerProps {
  question: Question
  answer: string | { id: string; label: string }[] | null | undefined
}

const getAnswer = (participant: Participant | null, questionId: string) => {
  const foundAnswer = participant?.answers?.find((a) => a.questionId === questionId)
  return (
    (foundAnswer?.options.length && foundAnswer?.options) ||
    (foundAnswer?.answer !== '' && foundAnswer?.answer) ||
    null
  )
}

const Answer: React.FC<AnswerProps> = ({ question, answer }) => {
  const ratingLabels = question.scale.reduce<Record<number, string>>((acc, item) => {
    const label = { [item.value]: `${item.value}. ${item.text}` }
    return { ...acc, ...label }
  }, {})

  switch (question.questionType) {
    case 'multiple_choice':
      if (question.maxChoices && question.maxChoices === 1) {
        return (
          <SingleChoiceAnswer
            mandatory={question.mandatory}
            selectedOption={(Array.isArray(answer) && answer[0]?.id) || ''}
            options={question.options || []}
          />
        )
      } else {
        return (
          <MultipleChoiceAnswer
            mandatory={question.mandatory}
            selectedOptions={Array.isArray(answer) ? answer : []}
            options={question.options || []}
          />
        )
      }
      break

    case 'number':
      return <NumberAnswer mandatory={question.mandatory} answer={Number(answer)} />

    case 'rating':
      return (
        <RatingAnswer
          ratingLabels={ratingLabels}
          question={question}
          answer={Number(answer) || null}
        />
      )

    case 'text':
      return <RichTextDisplay content={(typeof answer === 'string' && answer) || ''} />

    default:
      return <Text.Normal>{(typeof answer === 'string' && answer) || 'N/A'}</Text.Normal>
  }
}

const NumberAnswer: React.FC<NumberAnswerProps> = ({ answer, mandatory }) => (
  <NumberField mandatory={mandatory} maxDecimals={0} value={answer} onChange={() => {}} readonly />
)

const QuestionAnswer: React.FC<{
  question: Question
  answerComponent: JSX.Element
}> = ({ question, answerComponent }) => (
  <Box
    padding="s24"
    background="grey100"
    flexDirection="column"
    style={{ marginBottom: '24px' }}
    border={{
      all: { width: 's1', style: 'solid', color: 'grey100' },
    }}
    borderRadius={{ all: 'abs016' }}
  >
    <Box gap="s16" id={question.id}>
      <Box flexDirection="row" gap="s16">
        <NumberBadge label={Number(question.position)} />
        <Text.Normal size="300" weight="medium" whiteSpace="breakSpaces">
          {question.label}
        </Text.Normal>
      </Box>
      {answerComponent}
    </Box>
  </Box>
)

const AnswersSidePanel: React.FC<Props> = ({ participant, data, question }) => {
  if (question) {
    const answer = getAnswer(participant, question.id)
    return (
      <Box gap="s16">
        <QuestionAnswer
          question={question}
          answerComponent={<Answer answer={answer} question={question} />}
        />
      </Box>
    )
  }

  return (
    <Box gap="s16">
      {data.map((item) => {
        if (item.itemType === 'section') {
          return (
            <Box
              key={item.id}
              paddingTop="s24"
              background="grey100"
              flexDirection="column"
              style={{ marginBottom: '24px' }}
              border={{
                all: { width: 's1', style: 'solid', color: 'grey200' },
              }}
              borderRadius={{ all: 'abs016' }}
            >
              <Box paddingLeft="s24">
                <Text.Header2>{item.title}</Text.Header2>
              </Box>
              {item.questions?.map((q) => {
                const answer = getAnswer(participant, q.id)
                return (
                  <QuestionAnswer
                    key={q.id}
                    question={q}
                    answerComponent={<Answer question={q} answer={answer} />}
                  />
                )
              })}
            </Box>
          )
        }

        if (item.itemType === 'question') {
          const answer = getAnswer(participant, item.id)
          return (
            <QuestionAnswer
              key={item.id}
              question={item}
              answerComponent={<Answer answer={answer} question={item} />}
            />
          )
        }

        return null
      })}
    </Box>
  )
}

export default AnswersSidePanel
